import React, { Component } from 'react';
import Breadcrumbs from '../../shared/breadcrumbs/Breadcrumbs';
import '../Technical.css';

class SamlSSO extends Component {
    render() {
        const sso = process.env.BASE_URL + 'images/technical/SSO.jpg';
        return (
            <div className="technical-main">
                <Breadcrumbs />
                <div className="technical-content">
                    <h1 className="main-heading">
                    Implementation Guide for SAML SSO integration for login
                </h1>
                <h2 className="main-subheading">Overview</h2>
                <div className="technical-main-content">
                        <p>SAML simplifies Single Sign on (SSO) processes for users, Identity providers, and service providers.
                            SAML provides a solution to allow your identity provider and service providers to exist separately
                            from each other. This lets Identity provider deal with user management and provides access to
                            service provider. Service provider does not have to deal with user management.</p>
                        <p>
                            SAML implements a secure method of passing user authentications and authorizations between the
                            identity provider and service providers. When a user require to login into a SAML enabled
                            application, they have to go through their identity provider. The identity provider authenticates the
                            user’s credentials (If not already done) and sends the authorization for the user to the service
                            provider, and the user is now able to use the application.
                        </p>
                    </div>
                    <h2 className="main-subheading">How Does SAML Work?</h2>
                    <div className="technical-main-content">
                        <p>
                            SAML works by passing information about users, logins, and attributes between the identity provider
                            and service providers. Each user logs in once to Single Sign On with the identify provider, and then
                            the identify provider can pass SAML attributes to the service provider when the user attempts to
                            access those services. The service provider requests the authorization and authentication from the
                            identify provider. Since both of those systems speak the same language – SAML – the user only
                            needs to log in once.
                        </p>
                    </div>
                    <h2 className="main-subheading">SAML Provider</h2>
                    <div className="technical-main-content">
                        <p>
                            A SAML provider is a service that helps a user access a service they need. There are two primary
                            types of SAML providers; service provider and identity provider.
                        </p>
                        <p>A <b>service provider</b> needs the authentication from the identity provider to grant authorization to the user.</p>
                        <p>An <b>identity provider</b> performs the authentication that the end user is who they say they are and
                        sends that data to the service provider along with the user’s access rights for the service.</p>
                        <p>
                            Microsoft Active Directory or Azure are common identity providers. Salesforce and other CRM
                            solutions are usually service providers, in that they depend on an identity provider for user
                            authentication.
                        </p>
                        <p><b>Please Note: Thales AllAboutMe service is a service provider</b></p>
                    </div>
                    <h2 className="main-subheading">SAML 2.0 service</h2>
                    <div className="technical-main-content">
                        <p>
                        SAML 2.0 service provides two options (flows) of integration.
                        </p>
                        <p><b>IdP-Initiated SSO</b><br/>
                            In this scenario, a user is logged on to the IdP and attempts to access a resource on a remote SP
                            server. The SAML assertion is transported to the SP via HTTP POST.
                        </p>
                        <p><b>SP-Initiated SSO</b><br/>
                            In this scenario a user attempts to access a protected resource directly on an SP Web site without
                            being logged on. The user does not have an account on the SP site, but does have a federated
                            account managed by a third-party IdP. The SP sends an authentication request to the IdP. Both the
                            request and the returned SAML assertion are sent through the user’s browser via HTTP POST.
                        </p>
                        <p><b>Please Note: Thales AllAboutMe service only provides IdP-initiated SSO integration.</b></p>
                    </div>
                    <h2 className="main-subheading">SAML Assertion</h2>
                    <div className="technical-main-content">
                        <p>
                            A SAML Assertion is the XML document that the identity provider sends to the service provider that
                            contains the user authorization. There are three different types of SAML Assertions – authentication,
                            attribute, and authorization decision.
                        </p>
                        <ul>
                            <li>
                                <p>Authentication assertions prove identification of the user and provide the time the user
                                logged in and what method of authentication they used (I.e., Kerberos, 2 factor, etc.)</p>
                            </li>
                            <li>
                                <p>The attribution assertion passes the SAML attributes to the service provider – SAML
                                    attributes are specific pieces of data that provide information about the user.</p>
                            </li>
                            <li>
                                <p>An authorization decision assertion says if the user is authorized to use the service or if the
                                    identify provider denied their request due to a password failure or lack of rights to the
                                    service.</p>
                            </li>
                        </ul>
                        <p><b>Please Note: Thales AllAboutMe SAML 2.0 integration is built for attribution assertion and a
                                username/userID is required to identify user.</b></p>
                    </div>
                    <h2 className="main-subheading">Processing Steps:</h2>
                    <div className="technical-content-display">
                        <img className="technical-image-alignment technical-image-size" src={sso} alt="IdP-initiated SAML 2.0 flow" />
                        <div className="technical-main-content technical-content-alignment">
                        <ul>
                        <li><p>A user access to SAML 2.0 IdP service in order to access AllAboutMe service.</p></li>
                        <li><p>(Optional) If user is not logged into IdP service or his or her session has expired then user is asked to login.</p></li>
                        <li><p>(Optional) Only after successful login user is allowed to access IdP service.</p></li>
                        <li><p>The user clicks a link or otherwise requests access to a protected SP resource from IdP. Optionally, the IdP retrieves attributes from the user data store.</p>
                        <p>The IdP’s SSO service returns an HTML form to the browser with a SAML response containing the authentication assertion and any additional attributes. The browser automatically posts the HTML form to the SP.</p>
                        </li>
                        <p><b>Note: SAML specifications require that POST responses be digitally signed.</b></p>
                        <li><p>If the signature and the assertion are valid, the SP establishes a session for the user and redirects the browser to the target resource.</p></li>
                        </ul>
                        <p>Each identity provider and service provider need to agree upon the configuration for SAML. Both
                            ends need to have the exact configuration for the SAML authentication to work.</p>
                        </div>
                    </div>
                    <h2 className="main-subheading">Implementation requirements</h2>
                    <div className="technical-main-content">
                        <p>
                            SAML integration with Thales AllAboutMe service requires sharing of following information
                            between Thales and client (IdP provider).
                        </p>
                        <p><b>Thales:</b><br/>
                        Thales will provide following details in order for Client to setup in their IdP service for the integration to work.</p>
                        <ul>
                            <li><p>SP Entity ID: <i>AllAboutMe</i></p></li>
                            <li><p>Endpoint URL: <i>&lt;Assertion URL&gt;</i> - Provided as part of the setup for each environment</p></li>
                            <li><p>Assertion method: <i>attribution assertion</i> – Username is required to in AllAboutMe DB</p></li>
                        </ul>
                        <p><b>Customer:</b><br/>
                        Customer will have to provide following details so that Thales can configure the SAML 2.0 integration.</p>
                        <ul>
                            <li><p>IdP Entity ID: Static entity ID for the IdP service.</p></li>
                            <li><p>Certificate: this is the certificate for verification of the data</p></li>
                            <li><p>Is request signed: True/False</p></li>
                            <li><p>Is assertion signed: True/False</p></li>
                        </ul>
                        <p><b>Please note: Customers can also provide metadata xml file instead of the above information.</b></p>
                    </div>
                    <h2 className="main-subheading">Glossary</h2>
                    <div className="technical-main-content">
                    <ul>
                            <li><p><b>AAM</b><br/>AllAboutMe – solution provided by Thales</p></li>
                            <li><p><b>SAML</b><br/>Security Assertion Markup Language</p></li>
                            <li><p><b>SP</b><br/>Service provided. In this case AAM (Thales)</p></li>
                            <li><p><b>SP-Initiated</b><br/>Initiated from service provider end</p></li>
                            <li><p><b>IdP</b><br/>Identity provider. In this case Customer’s user management service</p></li>
                            <li><p><b>IdP-Initiated</b><br/>Identity provider initiated SAML 2.0 request.</p></li>
                            <li><p><b>SSO</b><br/>Single Sign On – User have to sign in only once and only in Company’s network</p></li>
                        </ul>
                    </div>
                </div>
            </div>
        )
    }
}

export default SamlSSO;